import {
	getConfigurationById,
	getSensorById,
	getSystemConnections,
} from '@Actions';
import { getValue } from '@Selectors';
import { Configuration } from '@Types';
import { ResampleVerificationFields } from '@Types/sensorResample';
import { findResampleFieldsThatChanged } from '@Utils/sensorFormCompare';
import { useGlobalization } from '@innovyze/stylovyze';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export type SensorFormChanges = Record<ResampleVerificationFields, string>;

type ConfigurationSensorDetailReturn = [
	Configuration | undefined,
	Configuration | undefined,
	(current: Record<string, string>) => Partial<SensorFormChanges>,
];

const useConfigurationSensorDetail = (
	sensorId: string,
): ConfigurationSensorDetailReturn => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const {
		configurationById: { configuration },
		selectedConfig,
	} = useSelector(getValue).configurations;

	const {
		sensorById: { sensor },
	} = useSelector(getValue).sensors;

	const MODIFIED_FIELD_MESSAGE = useMemo<string>(
		() => t('Warning: Field modified'),
		[],
	);

	useEffect(() => {
		const payload = { sensorId: sensorId, configType: 'sensor' };
		dispatch(getConfigurationById(payload));
		dispatch(getSensorById(sensorId));
		dispatch(getSystemConnections());
	}, [sensorId]);

	const fullConfiguration = useMemo(() => {
		if (configuration) {
			return {
				...configuration,
				longitude: configuration.longitude ?? sensor?.x,
				latitude: configuration.latitude ?? sensor?.y,
			};
		}
	}, [configuration, sensor]);

	const validateAndGetConfChanges = useCallback(
		(current: Record<string, string>) => {
			if (!fullConfiguration) return {};

			const fields = findResampleFieldsThatChanged(
				current,
				fullConfiguration,
			);
			return fields.reduce((acc: Partial<SensorFormChanges>, key) => {
				acc[key as ResampleVerificationFields] = MODIFIED_FIELD_MESSAGE;
				return acc;
			}, {});
		},
		[fullConfiguration],
	);
	return [fullConfiguration, selectedConfig, validateAndGetConfChanges];
};

export default useConfigurationSensorDetail;
